"use client";
import { useDivisionsMenuQuery } from "@gettactic/hooks";
import * as React from "react";
import { CampusIcon } from "@cmp-src/icons/icons";
import { UnifiedSelect, SelectItem } from "@cmp-src/form/UnifiedSelect";
import { Division, DivisionsTree, Office } from "@gettactic/api";
import { OfficeIcon } from "@cmp-src/icons/TacticIcons/OfficeIcon";
import { cn } from "@gettactic/helpers";
import { ChevronDownIcon, MapIcon } from "lucide-react";

/** Synthetic select-item id prefix for "view this campus map" entries. */
const DIVISION_MAP_ITEM_PREFIX = "division-map:";

type Props = {
  /** Callback when selection changes - receives array of IDs for multiselect, single ID for single select */
  onChange: (ids: string[]) => void;
  /** Selected office IDs */
  offices?: string[];
  /** Default selected office ID (single select) */
  defaultValue?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Show the selected office name in trigger */
  showSelectedLabel?: boolean;
  /** Not implemented: has no effect. */
  allowClear?: boolean;
  /** Custom class for trigger */
  triggerClassName?: string;
  /** Enable multiselect mode */
  multiselect?: boolean;
  /** Show split view with selected items panel (multiselect only) */
  splitView?: boolean;
  /** Show Apply button for deferred selection */
  withApply?: boolean;
  /** Custom trigger element */
  trigger?: React.ReactNode;
  /** Placeholder text */
  placeholder?: string;
  /** Width of the dropdown content */
  contentWidth?: string;
  /** Visual variant - 'default' has border, 'ghost' has no border */
  variant?: "default" | "ghost";
  limitWidth?: boolean;
  /** When provided, campus divisions that have a map get a "View campus
   *  map" entry in their group; selecting it fires this instead of an
   *  office change. */
  onSelectDivisionMap?: (division: Division) => void;
  /** Division whose campus map is currently being viewed — its map entry
   *  shows as the selected item instead of the office (single select). */
  selectedDivisionMapId?: string | null;
  /** Offices to show greyed out and refuse — e.g. ones the current selection
   *  elsewhere in the form rules out. Selecting a whole division skips them. */
  unselectableOfficeIds?: string[];
  /** Shown under each unselectable office, so the block explains itself. */
  unselectableOfficeReason?: string;
  /** When set, only these offices are listed; divisions left empty are
   *  dropped. Omit to list all offices. */
  selectableOfficeIds?: string[];
  /** Name of the field, announced before the value by screen readers
   *  (default trigger only). */
  label?: string;
};

// Sort offices by weight (lower weight first)
function sortOfficesByWeight<T extends { weight?: number | null }>(
  offices: T[]
): T[] {
  return [...offices].sort((a, b) => {
    const weightA = a.weight ?? Infinity;
    const weightB = b.weight ?? Infinity;
    return weightA - weightB;
  });
}

/** Which offices cannot be picked, and the reason to show under them. */
type UnselectableOffices = {
  ids: Set<string>;
  reason?: string;
};

// Convert divisions tree to SelectItem format
function divisionsToSelectItems(
  tree: DivisionsTree,
  withDivisionMaps = false,
  unselectable?: UnselectableOffices,
  selectable?: Set<string>
): SelectItem<Office>[] {
  const items: SelectItem<Office>[] = [];

  const isHidden = (office: Office) =>
    selectable !== undefined && !selectable.has(office.id);

  const officeToItem = (office: Office): SelectItem<Office> => {
    const isUnselectable = unselectable?.ids.has(office.id) ?? false;
    return {
      id: office.id,
      name: office.name,
      keywords: [office.name],
      icon: <OfficeIcon className="h-4 w-4" />,
      data: office,
      disabled: isUnselectable || undefined,
      description: isUnselectable ? unselectable?.reason : undefined
    };
  };

  // First: Add divisions with their children (offices sorted by weight)
  if (tree.children?.length) {
    tree.children.forEach((division) => {
      const divisionItem: SelectItem<Office> = {
        id: division.id,
        name: division.name,
        keywords: [division.name],
        children: []
      };

      // Campus with a map: a "view map" entry at the top of the group.
      if (withDivisionMaps && division.type === "campus" && division.map_data) {
        divisionItem.children!.push({
          id: `${DIVISION_MAP_ITEM_PREFIX}${division.id}`,
          name: `${division.name} map`,
          keywords: [division.name, "map", "campus"],
          icon: <MapIcon className="h-4 w-4" />
        });
      }

      // Add offices in this division, sorted by weight
      if (division.offices?.length) {
        const sortedOffices = sortOfficesByWeight(division.offices);
        sortedOffices.forEach((office) => {
          if (isHidden(office)) return;
          divisionItem.children!.push(officeToItem(office));
        });
      }

      // Add nested divisions recursively. Without its offices: they were
      // added above, and the recursion would list them again as top-level
      if (division.children?.length) {
        const nestedItems = divisionsToSelectItems(
          { ...division, offices: [] },
          withDivisionMaps,
          unselectable,
          selectable
        );
        divisionItem.children!.push(...nestedItems);
      }

      // Only add if has children
      if (divisionItem.children!.length > 0) {
        items.push(divisionItem);
      }
    });
  }

  // Second: Add top-level offices (sorted by weight)
  if (tree.offices?.length) {
    const sortedOffices = sortOfficesByWeight(tree.offices);
    sortedOffices.forEach((office) => {
      if (isHidden(office)) return;
      items.push(officeToItem(office));
    });
  }

  return items;
}

// Find a division by ID anywhere in the divisions tree. Exported for
// hosts that resolve a division from a shared URL (e.g. /map restoring
// a campus map from ?divisionId=…).
export function findDivisionById(
  tree: DivisionsTree,
  id: string
): DivisionsTree | null {
  for (const division of tree.children || []) {
    if (division.id === id) return division;
    const found = findDivisionById(division as DivisionsTree, id);
    if (found) return found;
  }
  return null;
}

// Find an item by ID in the tree
function findItemById(
  items: SelectItem<Office>[],
  id: string
): SelectItem<Office> | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * The bare office select behind `OfficeSelectorInput`, for toolbars and page headers
 * (`variant="ghost"`). On the map it can also list each campus map (`onSelectDivisionMap`).
 */
export const SelectOffices = ({
  onChange,
  offices = [],
  defaultValue,
  disabled,
  showSelectedLabel = true,
  allowClear = true,
  triggerClassName,
  multiselect = false,
  splitView = false,
  withApply = false,
  trigger,
  placeholder = "Select an office",
  contentWidth,
  variant = "default",
  limitWidth = true,
  onSelectDivisionMap,
  selectedDivisionMapId,
  unselectableOfficeIds,
  unselectableOfficeReason,
  selectableOfficeIds,
  label
}: Props) => {
  const divisions = useDivisionsMenuQuery();

  const items = React.useMemo(() => {
    if (!divisions.data?.tree) return [];
    return divisionsToSelectItems(
      divisions.data.tree,
      !!onSelectDivisionMap,
      unselectableOfficeIds && unselectableOfficeIds.length > 0
        ? {
            ids: new Set(unselectableOfficeIds),
            reason: unselectableOfficeReason
          }
        : undefined,
      selectableOfficeIds ? new Set(selectableOfficeIds) : undefined
    );
  }, [
    divisions.data?.tree,
    onSelectDivisionMap,
    unselectableOfficeIds,
    unselectableOfficeReason,
    selectableOfficeIds
  ]);

  // For single select
  const initialValue = offices.length > 0 ? offices[0] : defaultValue;
  const [selectedValue, setSelectedValue] = React.useState<string | undefined>(
    initialValue
  );

  // Sync selectedValue with offices prop for single select
  const officesKey = offices.join(",");
  React.useEffect(() => {
    if (!multiselect && offices.length > 0) {
      setSelectedValue(offices[0]);
    } else if (!multiselect && offices.length === 0 && !defaultValue) {
      setSelectedValue(undefined);
    }
  }, [officesKey, multiselect, defaultValue]);

  // For multiselect
  const selectedItems = React.useMemo(() => {
    if (!multiselect) return [];
    return offices
      .map((id) => findItemById(items, id))
      .filter((item): item is SelectItem<Office> => item !== null);
  }, [offices, items, multiselect]);

  // For single select
  const selectedItem = React.useMemo(() => {
    if (multiselect) return null;
    // A campus map being viewed wins over the office selection so the
    // dropdown reflects what's actually on screen.
    if (selectedDivisionMapId) {
      return findItemById(
        items,
        `${DIVISION_MAP_ITEM_PREFIX}${selectedDivisionMapId}`
      );
    }
    if (!selectedValue || items.length === 0) return null;
    return findItemById(items, selectedValue);
  }, [items, selectedValue, multiselect, selectedDivisionMapId]);

  const handleSelect = (item: SelectItem<Office> | null) => {
    // Campus map entries fire their own callback instead of an office
    // change; the office selection stays as it was.
    if (item?.id.startsWith(DIVISION_MAP_ITEM_PREFIX)) {
      const divisionId = item.id.slice(DIVISION_MAP_ITEM_PREFIX.length);
      const division = divisions.data?.tree
        ? findDivisionById(divisions.data.tree, divisionId)
        : null;
      if (division) onSelectDivisionMap?.(division);
      return;
    }
    const newValue = item?.id ?? "";
    setSelectedValue(newValue || undefined);
    onChange(newValue ? [newValue] : []);
  };

  const handleSelectMultiple = (items: SelectItem<Office>[]) => {
    const ids = items.map((item) => item.id);
    onChange(ids);
  };

  if (!divisions.data) {
    return null;
  }

  // Default trigger content
  const getDefaultTriggerContent = () => {
    if (multiselect) {
      if (selectedItems.length === 0) {
        return (
          <>
            <CampusIcon className="h-4 w-4 shrink-0" />
            {showSelectedLabel && (
              <span className="truncate font-inter text-content">
                {placeholder}
              </span>
            )}
          </>
        );
      }
      if (selectedItems.length === 1) {
        return (
          <>
            <CampusIcon className="h-4 w-4 shrink-0" />
            {showSelectedLabel && (
              <span className="truncate font-inter">
                {selectedItems[0].name}
              </span>
            )}
          </>
        );
      }
      return (
        <>
          <CampusIcon className="h-4 w-4 shrink-0" />
          {showSelectedLabel && (
            <span className="truncate font-inter">
              {selectedItems.length} selected
            </span>
          )}
        </>
      );
    }

    // Single select
    if (selectedItem) {
      return (
        <>
          <CampusIcon className="h-4 w-4 shrink-0" />
          {showSelectedLabel && (
            <span className="truncate">{selectedItem.name}</span>
          )}
        </>
      );
    }
    return (
      <>
        <CampusIcon className="h-4 w-4 shrink-0" />
        {showSelectedLabel && (
          <span className="truncate text-content">{placeholder}</span>
        )}
      </>
    );
  };

  const triggerStyles =
    variant === "default"
      ? "flex h-10 w-full min-w-0 items-center justify-between gap-1 cursor-pointer text-sm rounded-lg border border-input bg-background px-3 py-2 hover:bg-accent/50"
      : cn(
          "flex h-9 min-w-0 items-center gap-1 cursor-pointer text-sm",
          limitWidth ? "max-w-[200px]" : undefined
        );

  const triggerContent = trigger || (
    <span className={cn(triggerStyles, triggerClassName)}>
      <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {label && <span className="sr-only">{label}: </span>}
        {getDefaultTriggerContent()}
      </span>
      <ChevronDownIcon className={"size-4"} />
    </span>
  );

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className={cn(
          triggerStyles,
          "opacity-50 cursor-not-allowed",
          triggerClassName
        )}
      >
        {label && <span className="sr-only">{label}: </span>}
        {getDefaultTriggerContent()}
      </button>
    );
  }

  if (multiselect) {
    return (
      <UnifiedSelect
        items={items}
        values={selectedItems}
        onSelectMultiple={handleSelectMultiple}
        multiselect
        withApply={withApply}
        splitView={splitView}
        searchable
        hierarchical
        selectableParents
        searchPlaceholder="Search offices..."
        placeholder={placeholder}
        contentWidth={contentWidth || "280px"}
        trigger={triggerContent}
      />
    );
  }

  return (
    <UnifiedSelect
      items={items}
      value={selectedItem}
      onSelect={handleSelect}
      searchable
      hierarchical
      searchPlaceholder="Search offices..."
      placeholder={placeholder}
      contentWidth={contentWidth || "280px"}
      trigger={triggerContent}
    />
  );
};
